import React from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BTN = "border-2 border-black px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-100";

async function leave(path, to) {
  try { await api.post(path); } catch { /* still leave, cookies are cleared server-side when possible */ }
  window.location.assign(to);
}

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Page crashed:", error, info?.componentStack);
  }

  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { impersonating } = this.props;
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="app-crash-screen">
        <div className="border-2 border-black bg-white p-6 sm:p-8 max-w-md w-full">
          <div className="label-mono">Something went wrong</div>
          <h1 className="font-chivo font-black text-2xl uppercase tracking-tighter mt-1">This page couldn&apos;t load</h1>
          <p className="text-sm text-zinc-700 mt-3">
            {impersonating
              ? `You're viewing as ${impersonating}. Go back to your own account, then try again.`
              : "Try again, or sign out and back in."}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {impersonating && (
              <button type="button" className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="crash-end-impersonation"
                onClick={() => leave("/auth/end-impersonation", "/")}>
                Back to my admin view
              </button>
            )}
            <button type="button" className={`${BTN} bg-white hover:bg-zinc-100`} data-testid="crash-reload" onClick={() => window.location.reload()}>
              Try again
            </button>
            <button type="button" className={`${BTN} bg-white hover:bg-zinc-100`} data-testid="crash-sign-out"
              onClick={() => leave("/auth/logout", "/login")}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/** Catches page crashes so nobody is stuck on a blank screen (always offers a way back or out). */
export function AppErrorBoundary({ children }) {
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  return (
    <Boundary resetKey={pathname} impersonating={user?.impersonated_by ? user.email : null}>
      {children}
    </Boundary>
  );
}
